import { knownIconAsset } from '../../../../constants/payment-method-icon.const';
import type {
  CardBrand,
  EWalletBrand,
  PaymentMethodBrand,
  PaymentMethodCategory
} from '../../../../types/registration-payment.types';

export interface PaymentMethodConfig {
  label: string;
  /** Absolute public URL path for the logo image */
  logo: string;
  /** Whether the Important Notes dialog should be shown before redirecting to gateway */
  requiresImportantNotes: boolean;
  category: PaymentMethodCategory;
}

/**
 * PaymentMethodBrand plus the two DuitNow AutoDebit config keys (no brand — DuitNow is a category,
 * not a brand), plus DuitNow QR, which is only ever shown on a transaction (it is paid by scanning,
 * never saved).
 */
export type PaymentMethodConfigKey =
  PaymentMethodBrand | 'duitnow_consent' | 'duitnow_id' | 'duitnow_qr';

export const PAYMENT_METHOD_CONFIG: Record<
  PaymentMethodConfigKey,
  PaymentMethodConfig
> = {
  visa: {
    label: 'Visa',
    logo: knownIconAsset('visa', 'light'),
    requiresImportantNotes: true,
    category: 'cards'
  },
  mastercard: {
    label: 'Mastercard',
    logo: knownIconAsset('mastercard', 'light'),
    requiresImportantNotes: true,
    category: 'cards'
  },
  amex: {
    label: 'AMEX',
    logo: knownIconAsset('amex', 'light'),
    requiresImportantNotes: true,
    category: 'cards'
  },
  unionpay: {
    label: 'UnionPay',
    logo: knownIconAsset('unionpay', 'light'),
    requiresImportantNotes: true,
    category: 'cards'
  },
  grabpay: {
    label: 'GrabPay',
    logo: knownIconAsset('grabpay', 'light'),
    requiresImportantNotes: false,
    category: 'ewallets'
  },
  tng: {
    label: "Touch 'n Go eWallet",
    logo: knownIconAsset('tng', 'light'),
    requiresImportantNotes: false,
    category: 'ewallets'
  },
  shopeepay: {
    label: 'ShopeePay',
    logo: knownIconAsset('shopeepay', 'light'),
    requiresImportantNotes: false,
    category: 'ewallets'
  },
  duitnow_consent: {
    label: 'DuitNow AutoDebit · Your bank',
    logo: knownIconAsset('duitnow', 'light'),
    requiresImportantNotes: false,
    category: 'autodebit'
  },
  duitnow_id: {
    label: 'DuitNow AutoDebit · DuitNow ID',
    logo: knownIconAsset('duitnow', 'light'),
    requiresImportantNotes: false,
    category: 'autodebit'
  },
  duitnow_qr: {
    label: 'DuitNow QR',
    logo: knownIconAsset('duitnow', 'light'),
    requiresImportantNotes: false,
    category: 'ewallets'
  }
};

export const CARD_BRANDS: CardBrand[] = [
  'visa',
  'mastercard',
  'amex',
  'unionpay'
];

export const EWALLET_BRANDS: EWalletBrand[] = ['grabpay', 'tng', 'shopeepay'];

/** Maps resolved payment gateway name to whether Important Notes (RM 1.00 notice) should be shown. */
export const GATEWAY_REQUIRES_IMPORTANT_NOTES: Record<string, boolean> = {
  fiuu: true,
  stripe: false
};

/**
 * Whether to show the Important Notes before sending a brand to its gateway: decided by the
 * resolved gateway when known, otherwise by the brand's static config.
 */
export function requiresImportantNotes(
  brand: PaymentMethodBrand,
  gateway?: string
): boolean {
  return (
    (gateway ? GATEWAY_REQUIRES_IMPORTANT_NOTES[gateway] : undefined) ??
    PAYMENT_METHOD_CONFIG[brand]?.requiresImportantNotes ??
    false
  );
}
